/**
 * Cosa vedono i colleghi dei miei calendari esterni (Google, Outlook), con il tetto deciso dall'azienda.
 * Logica pura, testata in __tests__/profile.test.ts.
 */
export type ExternalLevel = 'none' | 'busy' | 'title' | 'details';
export type ExternalMax = 'busy' | 'title' | 'details';
export type Provider = 'google' | 'microsoft';

export const EXTERNAL_LEVELS: ExternalLevel[] = ['none', 'busy', 'title', 'details'];

export const EXTERNAL_LABELS: Record<ExternalLevel, string> = {
  none: 'Niente',
  busy: 'Solo occupato',
  title: 'Titolo',
  details: 'Tutti i dettagli',
};

const RANK: Record<ExternalLevel, number> = { none: 0, busy: 1, title: 2, details: 3 };

/** Senza una scelta dell'azienda il tetto e' "solo occupato": la scelta piu' prudente. */
export const DEFAULT_EXTERNAL_MAX: ExternalMax = 'busy';

export function externalKey(provider: Provider, calendarId: string): string {
  return `${provider}:${calendarId}`;
}

export function isAllowed(level: ExternalLevel, max: ExternalMax | undefined | null): boolean {
  return RANK[level] <= RANK[max || DEFAULT_EXTERNAL_MAX];
}

/** I livelli scelti dall'azienda e quelli oltre il tetto (da mostrare in grigio). */
export function levelOptions(max: ExternalMax | undefined | null): { level: ExternalLevel; allowed: boolean }[] {
  return EXTERNAL_LEVELS.map(level => ({ level, allowed: isAllowed(level, max) }));
}

/** Il livello effettivo: la mia scelta, ma mai oltre il tetto. Senza scelta: niente (nessuno vede nulla finche' non decido). */
export function effectiveLevel(chosen: ExternalLevel | undefined, max: ExternalMax | undefined | null): ExternalLevel {
  const c = chosen ?? 'none';
  if (isAllowed(c, max)) return c;
  return max || DEFAULT_EXTERNAL_MAX;
}

export function setExternalLevel(
  current: Record<string, ExternalLevel> | undefined,
  key: string,
  level: ExternalLevel,
  max: ExternalMax | undefined | null,
): Record<string, ExternalLevel> {
  return { ...(current || {}), [key]: effectiveLevel(level, max) };
}

/** Accende o spegne "Mi rende occupata" per un calendario, senza duplicati. */
export function toggleBusyCalendar(current: readonly string[] | undefined, key: string, on: boolean): string[] {
  const rest = (current || []).filter(k => k !== key);
  return on ? [...rest, key] : rest;
}
